import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { computeTotal, defaultLineItems } from "@/mocks/quotationsData";
import type { QuotationLineItem } from "@/mocks/quotationsData";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";
import { QuotationLineItems } from "../components/QuotationLineItems";

export default function QuotationTemplatesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [templateAuthor, setTemplateAuthor] = useState("Admin");
  const [templateItems, setTemplateItems] = useState<QuotationLineItem[]>(defaultLineItems);
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi<any[]>("/quotations/templates", undefined, []);
      const list = Array.isArray(res) ? res : (res as any)?.data || [];
      setTemplates(list);
    } catch (err: any) {
      console.error("Error loading templates:", err);
      showToast("Failed to load quotation templates", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const formatCurrency = (val: number) => "₹" + Number(val || 0).toLocaleString("en-IN");

  const formatDate = (d: string) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setTemplateDesc("");
    setTemplateAuthor("Admin");
    setTemplateItems([
      { id: 1, description: "Standard Package Item", qty: 1, unit: "Unit", rate: 25000, taxPercent: 18 },
    ]);
    setModalOpen(true);
  };

  const handleOpenEdit = (tmpl: any) => {
    setEditingTemplate(tmpl);
    setTemplateName(tmpl.name || "");
    setTemplateDesc(tmpl.description || "");
    setTemplateAuthor(tmpl.createdBy || tmpl.created_by || "Admin");
    const rawItems = tmpl.items || [];
    setTemplateItems(
      rawItems.map((item: any, idx: number) => ({
        id: idx + 1,
        description: item.description || "",
        qty: Number(item.qty) || 1,
        unit: item.unit || "Unit",
        rate: Number(item.rate) || 0,
        taxPercent: Number(item.taxPercent) || 0,
      }))
    );
    setModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      showToast("Please enter a template name", "error");
      return;
    }
    if (templateItems.length === 0) {
      showToast("Please add at least one line item", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await fetchApi(`/quotations/templates/${editingTemplate.id}`, {
          method: "PUT",
          body: JSON.stringify({
            name: templateName.trim(),
            description: templateDesc.trim(),
            created_by: templateAuthor,
            items: templateItems,
          }),
        });
        showToast("Template updated successfully", "success");
      } else {
        await fetchApi("/quotations/templates", {
          method: "POST",
          body: JSON.stringify({
            name: templateName.trim(),
            description: templateDesc.trim(),
            created_by: templateAuthor,
            items: templateItems,
          }),
        });
        showToast("Template created successfully", "success");
      }
      setModalOpen(false);
      loadTemplates();
    } catch (err: any) {
      showToast(err.message || "Failed to save template", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (tmpl: any) => {
    if (!window.confirm(`Are you sure you want to delete template "${tmpl.name}"?`)) return;
    try {
      await fetchApi(`/quotations/templates/${tmpl.id}`, { method: "DELETE" });
      showToast("Template deleted successfully", "success");
      loadTemplates();
    } catch (err: any) {
      showToast(err.message || "Failed to delete template", "error");
    }
  };

  return (
    <CrmLayout title="Quotation Templates">
      <div className="p-5 md:p-6 space-y-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <button onClick={() => navigate("/quotations")} className="hover:text-indigo-600 transition-colors cursor-pointer">
            Quotations
          </button>
          <i className="ri-arrow-right-s-line text-gray-300" />
          <span className="text-indigo-600 font-medium">Templates</span>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Quotation Templates</h1>
            <p className="text-sm text-gray-500 mt-0.5">Save and reuse common line-item presets</p>
          </div>
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            + New Template
          </button>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-indigo-600 mb-2" />
            <p className="text-sm text-gray-500">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <i className="ri-file-copy-line text-2xl text-gray-300" />
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-1">No templates yet</h3>
            <p className="text-sm text-gray-500 mb-4">Create your first template to speed up quotation building</p>
            <button
              onClick={handleOpenCreate}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              + Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-5">
            {templates.map((tmpl) => {
              const items = tmpl.items || [];
              const total = computeTotal(items, 0);
              return (
                <div
                  key={tmpl.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:border-indigo-300 transition-colors flex flex-col justify-between"
                >
                  <div>
                    {/* Card header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{tmpl.name}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          By {tmpl.createdBy || tmpl.created_by || "Admin"} · {formatDate(tmpl.createdAt || tmpl.created_at)}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold whitespace-nowrap flex-shrink-0">
                        {items.length} items
                      </span>
                    </div>

                    {/* Description */}
                    {tmpl.description && (
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2">{tmpl.description}</p>
                    )}

                    {/* Items preview */}
                    <div className="space-y-1 mb-4">
                      {items.slice(0, 3).map((item: any, idx: number) => (
                        <p key={idx} className="text-xs text-gray-700 truncate">
                          <span className="text-gray-400">-</span> {item.description} — {formatCurrency(item.rate)}
                        </p>
                      ))}
                      {items.length > 3 && (
                        <p className="text-xs text-gray-400">+{items.length - 3} more items</p>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-100 mt-2">
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(total)}</span>
                    <div className="flex items-center gap-1.5 w-full sm:w-auto">
                      <button
                        onClick={() => navigate(`/quotations/new?useTemplate=${tmpl.id}`)}
                        className="flex-1 sm:flex-none px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Use Template
                      </button>
                      <button
                        onClick={() => handleOpenEdit(tmpl)}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer flex-shrink-0"
                        title="Edit"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(tmpl)}
                        className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create / Edit Template Modal */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setModalOpen(false)} />
            <div className="relative bg-white rounded-xl border border-gray-200 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-10">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl z-10">
                <h2 className="text-base font-semibold text-gray-900">
                  {editingTemplate ? "Edit Quotation Template" : "New Quotation Template"}
                </h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g. 2BHK Standard Package"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                  <input
                    type="text"
                    value={templateDesc}
                    onChange={(e) => setTemplateDesc(e.target.value)}
                    placeholder="Short description of this template..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Preset Line Items</label>
                  <QuotationLineItems items={templateItems} onChange={setTemplateItems} />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-xl">
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  {saving ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-save-line" />}
                  {editingTemplate ? "Update Template" : "Save Template"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}